import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ShieldCheck,
  Paintbrush2,
  ArrowRight,
  FolderKanban,
  Palette,
  FileText,
  Users,
  Award,
  Briefcase,
} from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

/**
 * /painters/join — public ColourMatch Pro landing page.
 *
 * Stage 1 · 2026-02 · Product Direction Lock — repurposed from the
 * previous "List your painting business" recruitment page. The join
 * pathway (link to /pro/register / /painters/apply) is preserved so
 * painters can still apply and become eligible for the ColourMatch
 * Painters listing.
 *
 * Pro pricing is fetched live from GET /api/membership/tiers so there
 * is a single source of truth. If the fetch fails, we degrade to a
 * neutral "See current pricing" affordance rather than hard-coding.
 */
export default function PaintersJoin() {
  usePageMeta({
    title: "ColourMatch Pro — professional client presentations for painters",
    description:
      "ColourMatch Pro helps independent painters create professional client presentations: client projects, colour workspace, visualisation proposals, customer management, and membership. Join the ColourMatch Painters listing.",
    path: "/painters/join",
  });

  const [tiers, setTiers] = useState(null);
  const [tiersError, setTiersError] = useState(false);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/membership/tiers`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setTiers((d && d.tiers) || []))
      .catch(() => setTiersError(true));
  }, []);

  const capabilities = [
    {
      icon: FolderKanban,
      title: "Client Projects",
      body: "Every client, every project, every revision — organised in one place. Immutable revision history keeps the record clean.",
    },
    {
      icon: Palette,
      title: "Colour Workspace",
      body: "A curated colour library plus paint-brand alias search. Apply colours on your client's own photo in seconds.",
    },
    {
      icon: FileText,
      title: "Visualisation Proposals",
      body: "Send a branded proposal — Scope, Investment, Terms, Acceptance — as a web link or a PDF. Homeowners accept electronically.",
    },
    {
      icon: Users,
      title: "Customers",
      body: "A lightweight CRM for your painting business. Notes, contacts, project history — always yours.",
    },
    {
      icon: Award,
      title: "Membership",
      body: "Active ColourMatch Pro members are the only painters shown to homeowners in the ColourMatch Painters listing.",
    },
    {
      icon: Briefcase,
      title: "Business tools",
      body: "Branding, business profile, PDF quotes and proposals. Everything designed to make you look sharp in front of a client.",
    },
  ];

  const formatAud = (n) =>
    typeof n === "number"
      ? `$${n.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`
      : null;

  return (
    <div className="bg-brand-bg min-h-screen">
      {/* Hero */}
      <section className="border-b border-brand-border bg-white" data-testid="pro-landing-hero">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
          <span className="inline-flex items-center gap-2 bg-brand-teal/10 text-brand-teal text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full">
            <ShieldCheck size={13} /> ColourMatch Pro
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-brand-ink leading-[1.05]">
            Professional client presentations for painters.
          </h1>
          <p className="mt-5 text-lg text-brand-muted max-w-2xl leading-relaxed">
            ColourMatch Pro is the workspace behind the visualisations your clients see.
            Build proposals, manage projects, and win more work — while staying your own business.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/pro/register"
              data-testid="pro-landing-join-cta"
              className="inline-flex items-center gap-2 bg-brand-navy text-white h-12 px-6 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 hover:shadow-navy transition-all tap-spring"
            >
              Join ColourMatch Pro <ArrowRight size={16} />
            </Link>
            <Link
              to="/login"
              data-testid="pro-landing-signin-cta"
              className="inline-flex items-center gap-2 border border-brand-navy text-brand-navy h-12 px-6 rounded-2xl text-sm font-semibold hover:bg-brand-cream transition-all tap-spring"
            >
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* Who it&rsquo;s for */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-12 lg:py-16" data-testid="pro-landing-who">
        <div className="bg-white border border-brand-border rounded-3xl p-8 lg:p-10">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-3">Who it&rsquo;s for</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-brand-ink leading-tight max-w-3xl">
            Independent painters and small painting businesses in Australia.
          </h2>
          <p className="mt-4 text-brand-muted max-w-3xl leading-relaxed">
            You do the painting. ColourMatch Pro does the presentation.
            If you already spend evenings mocking up colours in Photoshop, cutting-and-pasting quotes into email,
            or explaining renders on your phone — this is for you.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0">
                <Paintbrush2 size={16} />
              </span>
              <div>
                <div className="text-sm font-semibold text-brand-ink">Independent painters</div>
                <div className="text-xs text-brand-muted">Owner-operators serving residential clients.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0">
                <Briefcase size={16} />
              </span>
              <div>
                <div className="text-sm font-semibold text-brand-ink">Small painting businesses</div>
                <div className="text-xs text-brand-muted">Teams of two to ten who want to look like a design studio.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-12 lg:pb-16" data-testid="pro-landing-capabilities">
        <div className="mb-8">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-3">what&rsquo;s inside</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-brand-ink leading-tight">
            Everything you need to present colour like a professional.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {capabilities.map((c) => (
            <div
              key={c.title}
              data-testid={`pro-capability-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-white border border-brand-border rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <span className="w-10 h-10 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-4">
                <c.icon size={18} />
              </span>
              <h3 className="text-base font-bold text-brand-ink mb-1.5">{c.title}</h3>
              <p className="text-sm text-brand-muted leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pro pricing (live from /api/membership/tiers) */}
      <section className="border-t border-brand-border bg-white" data-testid="pro-landing-pricing">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
          <div className="mb-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-3">Membership</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-brand-ink leading-tight">
              Pro pricing — two tiers, no hidden fees.
            </h2>
            <p className="mt-2 text-sm text-brand-muted">Billed monthly or annually. Cancel any time — your work is always yours.</p>
          </div>

          {tiersError && (
            <div className="border border-brand-border rounded-2xl p-6 bg-brand-sand text-sm text-brand-muted" data-testid="pro-pricing-fallback">
              We couldn&rsquo;t load live pricing right now. <Link to="/pro/register" className="text-brand-teal font-semibold">Start your application</Link> and you&rsquo;ll see the current tiers on the next step.
            </div>
          )}

          {!tiersError && !tiers && (
            <div className="text-sm text-brand-muted" data-testid="pro-pricing-loading">Loading current pricing…</div>
          )}

          {tiers && tiers.length > 0 && (
            <div className="grid md:grid-cols-2 gap-5">
              {tiers.filter((t) => t.active).map((t) => (
                <div
                  key={t.slug}
                  data-testid={`pro-pricing-tier-${t.slug}`}
                  className="border border-brand-border rounded-3xl p-7 bg-brand-bg flex flex-col"
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-xl font-extrabold text-brand-ink">{t.name}</h3>
                    {typeof t.price_monthly_aud === "number" && (
                      <div className="text-right">
                        <div className="text-3xl font-extrabold text-brand-ink leading-none">
                          {formatAud(t.price_monthly_aud)}
                          <span className="text-sm font-medium text-brand-muted">/mo</span>
                        </div>
                        {typeof t.price_annual_aud === "number" && (
                          <div className="text-xs text-brand-muted mt-1">or {formatAud(t.price_annual_aud)} / year</div>
                        )}
                      </div>
                    )}
                  </div>

                  {Array.isArray(t.features_confirmed) && t.features_confirmed.length > 0 && (
                    <ul className="mt-5 space-y-2 text-sm text-brand-ink flex-1">
                      {t.features_confirmed.slice(0, 6).map((f, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-teal flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Link
                    to="/pro/register"
                    data-testid={`pro-pricing-tier-${t.slug}-cta`}
                    className="mt-6 inline-flex items-center justify-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 transition-all"
                  >
                    Join {t.name} <ArrowRight size={14} />
                  </Link>
                </div>
              ))}
            </div>
          )}

          <p className="mt-6 text-xs text-brand-muted">
            Pricing shown is fetched live from ColourMatch Pro membership configuration. Onboarding fees, taxes and Stripe processing may apply at checkout.
          </p>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-14 lg:py-20" data-testid="pro-landing-closing">
        <div className="bg-brand-ink text-white rounded-3xl p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-navy/25 blur-3xl pointer-events-none" />
          <div className="relative max-w-2xl">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-2">
              Ready when you are
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
              Join ColourMatch Pro and start presenting like a design studio.
            </h2>
            <p className="mt-4 text-white/75 leading-relaxed">
              Active approved ColourMatch Pro members are the only painters shown to homeowners in the ColourMatch Painters listing.
              You keep your business, your clients, and your work — always.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/pro/register"
                data-testid="pro-landing-closing-join-cta"
                className="inline-flex items-center gap-2 bg-white text-brand-ink h-12 px-6 rounded-2xl text-sm font-semibold hover:bg-brand-sand transition-all"
              >
                Join ColourMatch Pro <ArrowRight size={16} />
              </Link>
              <Link
                to="/login"
                data-testid="pro-landing-closing-signin-cta"
                className="inline-flex items-center gap-2 border border-white/40 text-white h-12 px-6 rounded-2xl text-sm font-semibold hover:bg-white/10 transition-all"
              >
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
